import { Target, ArrowRight, Clock, Zap } from 'lucide-react';
import type { RepositoryAnalysis } from '../types/analysis.types';

interface QuickWinsPanelProps {
  data: RepositoryAnalysis;
  onNavigateToRecommendations: () => void;
}

export const QuickWinsPanel: React.FC<QuickWinsPanelProps> = ({
  data,
  onNavigateToRecommendations,
}) => {
  const quickWins = data?.quickWins || [];

  if (quickWins.length === 0) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-slate-200 dark:border-slate-700/50 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Target className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-primary">Quick Wins</h2>
              <p className="text-sm text-secondary">High-impact fixes you can implement today</p>
            </div>
          </div>
          <button
            onClick={onNavigateToRecommendations}
            className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
          >
            View all {data.recommendationsCount ?? data.recommendations?.length ?? 0} findings
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* List */}
        <div className="divide-y divide-slate-100 dark:divide-slate-700/40">
          {quickWins.slice(0, 6).map((win, index) => (
            <div
              key={win.id}
              className="group flex items-center gap-0 hover:bg-amber-50/60 dark:hover:bg-amber-900/10 transition-colors cursor-pointer"
              onClick={onNavigateToRecommendations}
            >
              {/* Number column */}
              <div className="w-16 shrink-0 flex flex-col items-center justify-center self-stretch gap-1 border-r border-slate-100 dark:border-slate-700/40 group-hover:border-amber-200 dark:group-hover:border-amber-800/40 transition-colors">
                <span className="text-4xl font-black leading-none text-slate-100 dark:text-slate-700 group-hover:text-amber-200 dark:group-hover:text-amber-800 transition-colors select-none tabular-nums">
                  {index + 1}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 px-5 py-4">
                <p className="font-semibold text-sm text-primary group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors leading-snug">
                  {win.title}
                </p>
                {win.description && (
                  <p className="mt-1 text-xs text-secondary leading-relaxed">{win.description}</p>
                )}
              </div>

              {/* Right metadata */}
              <div className="shrink-0 pr-5 flex items-center gap-2">
                {win.effort && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-medium text-secondary group-hover:bg-amber-100 dark:group-hover:bg-amber-900/40 group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">
                    <Clock className="w-3 h-3" />
                    {win.effort}
                  </span>
                )}
                <Zap className="w-4 h-4 text-slate-200 dark:text-slate-700 group-hover:text-amber-400 dark:group-hover:text-amber-500 transition-colors" />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile CTA */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700/50 sm:hidden">
          <button
            onClick={onNavigateToRecommendations}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
          >
            View All {data.recommendationsCount ?? data.recommendations?.length ?? 0} Findings
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickWinsPanel;
